import {
	ComposeRequest,
	ComposeResponse,
	ContextListRequest,
	ContextListResponse,
	FeedbackRequest,
	FeedbackRequestApi,
	FeedbackResponse,
	FeedbackResponseApi,
	InsightRequest,
	InsightRequestApi,
	InsightResponse,
	InsightResponseApi,
	SampleRequest,
	SampleRequestApi,
	SampleResponse,
	SampleResponseApi,
	ScopeRequest,
	ScopeRequestApi,
	ScopeResponse,
	ScopeResponseApi,
	SessionRequest,
	SessionResponse
} from '@/types/conductor'
import {instance} from '@/api/axiosConfig'
import {AxiosRequestConfig, AxiosResponse} from 'axios'
import store from '../store'
import {applyRequestBaseFields, applyRequestBaseFieldsWithoutSession,} from '@/utils/requestUtils'
import {getRequestConfig, requestConfig} from '@/api/authorisation'
import {validate} from '@/utils/validator.util'
import {
	FeedbackRequestApiSchema,
	FeedbackResponseApiSchema,
	InsightRequestApiSchema,
	InsightResponseApiSchema,
	SampleRequestApiSchema,
	SampleResponseApiSchema,
	ScopeRequestApiSchema,
	ScopeResponseApiSchema,
} from '@/validators/conductor.validator'
import {buildServiceRequest} from '@/utils/service.util'
import {useProfilesStore} from '@/store/modules/userProfile'
import router from '@/router'
import {Capacitor} from '@capacitor/core'
import {App as CapacitorApp} from '@capacitor/app'
import {sanitiseComposeResponse} from "@/utils/composerResponseUtils";

export const VERSION = '2.1'

function getProtocol(): string {
	const stbApiIsSecure: boolean = store.getters['configModule/stbApiIsSecure']
	return stbApiIsSecure ? 'https' : 'http'
}

function getApiHost(): string {
	const stbApiHost: string = store.getters['configModule/stbApiHost']
	if (!stbApiHost || stbApiHost === '') {
		console.error('Missing stbApiHost')
		throw new Error('Missing stbApiHost')
	}
	return stbApiHost
}

async function getProtocolAndApiHost(): Promise<string> {
	const stbApiHost = store.getters['configModule/stbApiHost']
	const stbApiIsSecure = store.getters['configModule/stbApiIsSecure']
	if (!stbApiHost || stbApiHost === '') {
		console.error('MISSING stbApiHost')
		throw 'MISSING stbApiHost'
	}
	const protocol = stbApiIsSecure ? 'https' : 'http'
	return `${protocol}://${stbApiHost}`
}

// ----------------- CLIENT AGENT STRING -----------------
async function getClientAgentString(): Promise<string> {
	const currentPath = router.currentRoute.value.path
	let client = 'share-web'

	if (currentPath.includes('/odds')) {
		if (Capacitor.isNativePlatform()) {
			const platform = Capacitor.getPlatform();
			client = `share-odds-${platform}`
		} else {
			client = 'share-odds'
		}
	} else if (currentPath.includes('/betpic')) {
		if (Capacitor.isNativePlatform()) {
			const platform = Capacitor.getPlatform();
			client = `share-betpic-${platform}`
		} else {
			client = 'share-betpic'
		}
	} else if (currentPath.includes('/bet')) {
		if (Capacitor.isNativePlatform()) {
			const platform = Capacitor.getPlatform();
			client = `share-direct-${platform}`
		} else {
			client = 'share-direct'
		}
	}

	// Get user info from Pinia store
	const userProfileStore = useProfilesStore()
	const userId = userProfileStore?.profile?.userId ?? 0
	const username = userProfileStore?.profile?.username ?? 'Guest'

	if (Capacitor.isNativePlatform()) {
		try {
			const {version: appVersion} = await CapacitorApp.getInfo()
			return `client=${client};appVersion=${appVersion};version=${VERSION};userId=${userId};username=${username}`
		} catch (_) {
		}
	}

	return `client=${client};version=${VERSION};userId=${userId};username=${username}`
}

// ----------------- Helpers -----------------
async function getBaseRequestData() {
	return {
		requestId: Date.now(),
		clientAgentString: await getClientAgentString(),
	}
}

// ----------------- Axios Invokers -----------------
async function invokeGlobalScope(data: any): Promise<AxiosResponse> {
	const protocolAndApiHost = await getProtocolAndApiHost()
	const url = `${protocolAndApiHost}/bet-composer/composer/conductor/global`
	const config: AxiosRequestConfig = requestConfig()
	return instance.post(url, data, config)
}

async function invokeContextScope(context: string, data: any): Promise<AxiosResponse> {
	if (context === '') return Promise.reject('No context selected')
	const protocolAndApiHost = await getProtocolAndApiHost()
	const url = `${protocolAndApiHost}/bet-composer/composer/conductor/context/${context}`
	const config = requestConfig()
	return instance.post(url, data, config)
}

async function invokeContextRequest(context: string, data: any): Promise<AxiosResponse> {
	if (context === '') return Promise.reject('No context selected')
	const protocol = getProtocol()
	const apiHost = getApiHost()
	const url = `${protocol}://${apiHost}/bet-composer/composer/conductor/context/${context}`
	const config: AxiosRequestConfig = getRequestConfig()
	return instance.post(url, data, config)
}

// ----------------- API -----------------
export default {
	async getCompose(context: string, composeRequestTemplate: ComposeRequest): Promise<ComposeResponse> {
		const composeRequest = applyRequestBaseFields(composeRequestTemplate)
		Object.assign(composeRequest, await getBaseRequestData())
		const data = {composeRequest}
		const response: AxiosResponse = await invokeContextScope(context, data)
		return sanitiseComposeResponse(response.data.composeResponse)
	},

	async getSession(): Promise<SessionResponse> {
		const sessionRequestTemplate: SessionRequest = {}
		const sessionRequest = applyRequestBaseFieldsWithoutSession(sessionRequestTemplate)
		Object.assign(sessionRequest, await getBaseRequestData())
		const data = {sessionRequest}
		const response: AxiosResponse = await invokeGlobalScope(data)
		return response.data.sessionResponse
	},

	async getContextList(): Promise<ContextListResponse> {
		const contextListRequest: ContextListRequest = {
			contextListRequest: applyRequestBaseFields({
				...await getBaseRequestData(),
			}),
		}

		const data = {contextListRequest}
		const response: AxiosResponse = await invokeGlobalScope(data)
		return response.data.contextListResponse
	},

	async submitFeedback(context: string, feedbackRequestTemplate: FeedbackRequest): Promise<FeedbackResponse> {
		const feedbackRequest = applyRequestBaseFields(feedbackRequestTemplate)
		Object.assign(feedbackRequest, await getBaseRequestData())
		const data = {feedbackRequest}
		const response: AxiosResponse = await invokeContextScope(context, data)
		return response.data.feedbackResponse
	},

	async getBetBuilder(context: string, betBuilderBuildRequestTemplate: any) {
		const betBuilderRequest = applyRequestBaseFields(betBuilderBuildRequestTemplate)
		Object.assign(betBuilderRequest, await getBaseRequestData())
		const data = {bbBuildRequest: betBuilderRequest}
		const response: AxiosResponse = await invokeContextScope(context, data)
		return response.data.bbBuildResponse
	},

	async mergeBetBuilders(context: string, betBuilderMergeRequestTemplate: any) {
		const betBuilderMergeRequest = applyRequestBaseFields(betBuilderMergeRequestTemplate)
		Object.assign(betBuilderMergeRequest, await getBaseRequestData())
		const data = {bbMergeRequest: betBuilderMergeRequest}
		const response: AxiosResponse = await invokeContextScope(context, data)
		return response.data.bbMergeResponse
	},
}

// ----------------- Additional Explicit Functions -----------------
export async function submitFeedback(context: string, feedbackRequest: FeedbackRequest): Promise<FeedbackResponse> {
	const serviceRequest = buildServiceRequest()
	const feedbackRequestApi: FeedbackRequestApi = {
		feedbackRequest: {
			...serviceRequest,
			...feedbackRequest,
			...await getBaseRequestData(),
		},
	}
	validate(FeedbackRequestApiSchema, feedbackRequestApi)
	const axiosResponse: AxiosResponse = await invokeContextRequest(context, feedbackRequestApi)
	const feedbackResponseApi: FeedbackResponseApi = axiosResponse.data
	validate(FeedbackResponseApiSchema, feedbackResponseApi)
	return feedbackResponseApi.feedbackResponse
}

export async function getSamples(context: string, sampleRequest: SampleRequest): Promise<SampleResponse> {
	const serviceRequest = buildServiceRequest()
	const sampleRequestApi: SampleRequestApi = {
		sampleRequest: {
			...serviceRequest,
			...sampleRequest,
			...await getBaseRequestData(),
		},
	}
	validate(SampleRequestApiSchema, sampleRequestApi)
	const axiosResponse: AxiosResponse = await invokeContextRequest(context, sampleRequestApi)
	const sampleResponseApi: SampleResponseApi = axiosResponse.data
	validate(SampleResponseApiSchema, sampleResponseApi)
	return sampleResponseApi.sampleResponse
}

export async function getScopes(context: string, scopeRequest: ScopeRequest): Promise<ScopeResponse> {
	const serviceRequest = buildServiceRequest()
	const scopeRequestApi: ScopeRequestApi = {
		scopeRequest: {
			...serviceRequest,
			...scopeRequest,
			...await getBaseRequestData(),
		},
	}
	validate(ScopeRequestApiSchema, scopeRequestApi)
	const axiosResponse: AxiosResponse = await invokeContextRequest(context, scopeRequestApi)
	const scopeResponseApi: ScopeResponseApi = axiosResponse.data
	validate(ScopeResponseApiSchema, scopeResponseApi)
	return scopeResponseApi.scopeResponse
}

export async function getInsights(context: string, insightRequest: InsightRequest): Promise<InsightResponse> {
	const serviceRequest = buildServiceRequest()
	const insightRequestApi: InsightRequestApi = {
		insightRequest: {
			...serviceRequest,
			...insightRequest,
			...await getBaseRequestData(),
		},
	}
	validate(InsightRequestApiSchema, insightRequestApi)
	const axiosResponse: AxiosResponse = await invokeContextRequest(context, insightRequestApi)
	const insightResponseApi: InsightResponseApi = axiosResponse.data
	validate(InsightResponseApiSchema, insightResponseApi)
	return insightResponseApi.insightResponse
}